import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  Description,
  Explore,
  ContentCopy,
  Visibility,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { formatDateTime } from '../utils/formatting';
import { FilterableDataTable } from './filterable-data-table';
import { TemplateDiscoveryDialog } from './TemplateDiscoveryDialog';
import {
  useGetAdminOrgNoteTemplatesQuery,
  useDeleteAdminNoteTemplateMutation,
} from '../store/hooks';
import type { AdminNoteTemplateInfo } from '../store/noteTemplatesAdminApi';

interface OrganizationTemplatesSectionProps {
  organizationId: string;
}

export function OrganizationTemplatesSection({ organizationId }: OrganizationTemplatesSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [discoveryDialogOpen, setDiscoveryDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // API Queries - use new notetemplates API
  const {
    data: templatesData,
    isLoading,
    refetch,
  } = useGetAdminOrgNoteTemplatesQuery(
    { organizationId },
    { skip: !organizationId }
  );

  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteAdminNoteTemplateMutation();

  const templates = templatesData?.templates || [];
  const existingTemplateIds = templates.map(t => t.id);

  const handleConvertTemplate = useCallback(async (templateId: string) => {
    // This will be handled by TemplateDiscoveryDialog
    setDiscoveryDialogOpen(false);
    refetch();
  }, [refetch]);

  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    try {
      await deleteTemplate({ templateId }).unwrap();
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      setConfirmDelete(null);
      refetch();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to delete template', { variant: 'error' });
    }
  }, [deleteTemplate, enqueueSnackbar, refetch]);

  const columns: Array<{
    key: keyof AdminNoteTemplateInfo | 'actions';
    label: string;
    render?: (value: unknown, row: AdminNoteTemplateInfo) => React.ReactNode;
  }> = [
    {
      key: 'title',
      label: 'Template Name',
      render: (value: unknown) => (
        <Typography variant="body2" fontWeight={600}>
          {(value as string) || 'Unnamed Template'}
        </Typography>
      ),
    },
    {
      key: 'id',
      label: 'Template ID',
      render: (value: unknown) => {
        const id = value as string;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {id ? (id.length > 20 ? id.substring(0, 20) + '...' : id) : 'N/A'}
            </Typography>
            {id && (
              <Tooltip title="Copy ID">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    void navigator.clipboard.writeText(id);
                    enqueueSnackbar('Template ID copied', { variant: 'success' });
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      key: 'current_version',
      label: 'Version',
      render: (value: unknown) => (
        <Typography variant="body2" color="text.secondary">
          v{(value as number) || 1}
        </Typography>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: unknown) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDateTime(new Date(value as string)) : 'Unknown'}
        </Typography>
      ),
    },
    {
      key: 'updated_at',
      label: 'Modified',
      render: (value: unknown) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDateTime(new Date(value as string)) : 'Unknown'}
        </Typography>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: unknown, template: AdminNoteTemplateInfo) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="View Template Details">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                navigate(`/admin-v2/templates/${template.id}`);
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {confirmDelete === template.id ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="error">
                Delete?
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={() => handleDeleteTemplate(template.id)}
                disabled={isDeleting}
              >
                {isDeleting ? <CircularProgress size={16} /> : 'Yes'}
              </Button>
              <Button
                size="small"
                onClick={() => setConfirmDelete(null)}
              >
                No
              </Button>
            </Box>
          ) : (
            <Tooltip title="Delete Template">
              <IconButton
                size="small"
                color="error"
                onClick={e => {
                  e.stopPropagation();
                  setConfirmDelete(template.id);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Organization Templates"
        subheader={`${templates.length} template${templates.length !== 1 ? 's' : ''} configured`}
        action={
          <Button
            variant="contained"
            startIcon={<Explore />}
            onClick={() => setDiscoveryDialogOpen(true)}
          >
            Convert Template
          </Button>
        }
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Alert severity="info">
            No templates configured for this organization. Click "Convert Template" to convert a user template to organization scope.
          </Alert>
        ) : (
          <FilterableDataTable
            title=""
            columns={columns as any}
            data={templates}
            total={templates.length}
            loading={false}
            emptyMessage="No templates found"
          />
        )}
      </CardContent>

      {/* Template Discovery Dialog */}
      <TemplateDiscoveryDialog
        open={discoveryDialogOpen}
        onClose={() => setDiscoveryDialogOpen(false)}
        onSelect={handleConvertTemplate}
        organizationId={organizationId}
        existingTemplateIds={existingTemplateIds}
      />
    </Card>
  );
}